import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OverlayModule } from '@angular/cdk/overlay';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';

import * as AuthSelectors from '../../../auth/store/auth.selectors';
import { AdminUserTenant } from '../../../shared/api/types/GraphQL';
import { DialogButtonsText } from '../../../shared/api/types/enums';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { GuardStatusService } from '../../../shared/services/guard-status.service';
import { persistActiveTenant } from '../../../shared/utils/tenant-storage.util';
import { sortTenantsByLabel, tenantInitial, tenantLabel } from '../../../shared/utils/tenant-label.util';
import { TopbarMenuService } from '../topbar-menu.service';
import { ChromeReadyService } from '../../chrome-ready.service';

/** The tenant list earns a search field at this size (design canon §2). */
const SEARCH_THRESHOLD = 8;

/**
 * Tenant switcher (NBO-2331). The current tenant is pinned at the top with a
 * "Current" tag; the rest sort alphabetically. Switching is a hard reload —
 * the SPA rebuilds every tenant-scoped cache from scratch.
 */
@Component({
  selector: 'app-topbar-tenant-switcher',
  standalone: true,
  imports: [OverlayModule, FormsModule, TranslateModule, MatIconModule],
  templateUrl: './topbar-tenant-switcher.component.html',
  styleUrls: ['./topbar-tenant-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopbarTenantSwitcherComponent {
  // Shared across the five clusters so only one popover is open and switching
  // between triggers takes one click (see TopbarMenuService).
  readonly open = this.menu.openFor('tenant');
  readonly term = signal('');

  /** Shared with the rest of the identity cluster — one reveal (NBO-2331 r4). */
  readonly ready = inject(ChromeReadyService).identityReady;

  private readonly tenants = toSignal(this.store.select(AuthSelectors.selectAuthTenants), { initialValue: [] });
  readonly activeTenant = toSignal(this.store.select(AuthSelectors.selectActiveTenant), { initialValue: null });

  readonly showSearch = computed(() => this.tenants().length >= SEARCH_THRESHOLD);

  readonly currentLabel = computed(() => this.label(this.activeTenant()));

  /** Every tenant except the pinned current one, alphabetical, then filtered. */
  readonly otherTenants = computed<AdminUserTenant[]>(() => {
    const current = this.activeTenant();
    const needle = this.term().trim().toLowerCase();
    const matching = this.tenants()
      .filter((t) => t?.tenant !== current?.tenant)
      .filter((t) => !needle || this.label(t).toLowerCase().includes(needle));
    return sortTenantsByLabel(matching);
  });

  // Switching is a hard reload, which the CanDeactivate guard cannot intercept
  // — so with the top bar now visible inside the editor (NBO-2207) the switch
  // must ask before discarding unsaved changes itself.
  private readonly guardActive = toSignal(this.guardStatus.getGuardStatus(), { initialValue: false });

  constructor(
    private menu: TopbarMenuService,
    private store: Store,
    private guardStatus: GuardStatusService,
    private confirmDialog: ConfirmDialogService
  ) {}

  toggle(): void {
    this.menu.toggle('tenant');
    if (!this.open()) {
      this.term.set('');
    }
  }

  close(): void {
    this.menu.close();
    this.term.set('');
  }

  onBackdropClick(event: MouseEvent): void {
    this.term.set('');
    this.menu.handleBackdropClick(event);
  }

  onDetach(): void {
    this.menu.closeIf('tenant');
  }

  /** Template hooks for the shared label rules (tenant-label.util). */
  label(tenant: AdminUserTenant | null | undefined): string {
    return tenantLabel(tenant);
  }

  initial(tenant: AdminUserTenant | null | undefined): string {
    return tenantInitial(tenant);
  }

  switchTenant(tenant: AdminUserTenant): void {
    if (!this.guardActive()) {
      this.applySwitch(tenant);
      return;
    }
    this.close();
    this.confirmDialog
      .openCustom(
        'common.dialog.unsaved_title',
        'nav.topbar.tenant_switch_unsaved_message',
        'nav.topbar.tenant_switch_confirm'
      )
      .afterClosed()
      .subscribe((result) => {
        if (result === DialogButtonsText.CONFIRM) {
          this.applySwitch(tenant);
        }
      });
  }

  private applySwitch(tenant: AdminUserTenant): void {
    // Before the reload: the setTenants reducer reads this session key, and
    // the account's lastTenant row follows from the tenant that loads (NBO-2517).
    persistActiveTenant(tenant.id);
    window.location.href = '/home';
  }
}
